import type { FC, ReactNode } from 'react';
import React, { useCallback, useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/custom-theme-button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';

import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context';

import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../Feed';

import { FeedCardContext } from './FeedCardContext';

const styles = xcss({ color: 'color.text.subtle' });
const i18n = defineMessages({
	createdBy: {
		id: 'feed.card.byline.createdBy',
		defaultMessage: 'Created by {name}',
		description: 'Text to represent the creator of the page in the feed card byline',
	},
	ownedBy: {
		id: 'feed.card.byline.ownedBy',
		defaultMessage: 'Owned by {name}',
		description: 'Text to represent the owner of the page in the feed card byline',
	},
	profile: {
		id: 'feed.card.byline.profile',
		defaultMessage: 'profile',
		description: 'Aria label for author profile link',
	},
	space: {
		id: 'feed.card.byline.space',
		defaultMessage: 'space',
		description: 'Aria label for content space link',
	},
});

export const buttonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			fontWeight: token('font.weight.regular'),
			textDecoration: 'underline',
		},
		spinnerStyles,
	};
};

type LinkOrTextProps = {
	children: ReactNode;
	url?: string;
	analyticsId: string;
	label: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};

const LinkOrText: FC<LinkOrTextProps> = ({
	children,
	url,
	analyticsId,
	label,
	popularFeedTimeGranularity,
	followingFeedSortby,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const handleClick = useCallback(() => {
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: analyticsId,
			includeNavdex: true,
			additionalAttributes: {
				popularFeedTimeGranularity,
				followingFeedSortby,
			},
		});
	}, [fireAnalyticsEvent, analyticsId, popularFeedTimeGranularity, followingFeedSortby]);

	if (url) {
		return (
			<Button
				href={url}
				aria-label={label}
				onClick={handleClick}
				theme={buttonTheme}
				appearance="subtle-link"
				spacing="none"
			>
				{children}
			</Button>
		);
	}
	return <span>{children}</span>;
};

type FeedCardByLineProps = {
	spaceName?: string;
	spaceUrl?: string;
	authorName?: string;
	authorId?: string;
	ownerName?: string;
	ownerId?: string;
	timestamp?: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};

export const FeedCardByLine: FC<FeedCardByLineProps> = ({
	spaceName,
	spaceUrl,
	authorName,
	authorId,
	ownerName,
	ownerId,
	timestamp = '',
	popularFeedTimeGranularity,
	followingFeedSortby,
}) => {
	const authorUrl = getAtlassianPeopleProfileUrl(authorId, 'feed-alpha', CONTEXT_PATH);

	const ownerUrl = getAtlassianPeopleProfileUrl(ownerId, 'feed-alpha', CONTEXT_PATH);

	const intl = useIntl();

	const id = ownerId || authorId;
	const name = ownerName || authorName;
	const url = ownerUrl || authorUrl;
	const bylineText = ownerName ? i18n.ownedBy : i18n.createdBy;

	return (
		<Flex
			wrap="wrap"
			gap="space.050"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			xcss={styles}
		>
			{name && (
				<FormattedMessage
					tagName="div"
					{...bylineText}
					values={{
						name: (
							<LinkOrText
								label={`${name}, ${intl.formatMessage(i18n.profile)}`}
								url={url}
								analyticsId="feedCardContentAuthor"
								popularFeedTimeGranularity={popularFeedTimeGranularity}
								followingFeedSortby={followingFeedSortby}
							>
								<ProfileCardWrapper userId={id}>{name}</ProfileCardWrapper>
							</LinkOrText>
						),
					}}
				/>
			)}
			{name && timestamp && <>•</>}
			{timestamp && <span>{timestamp}</span>}
			{spaceName && (name || timestamp) && <>•</>}
			{spaceName && (
				<LinkOrText
					label={`${spaceName}, ${intl.formatMessage(i18n.space)}`}
					url={spaceUrl}
					analyticsId="feedCardSpace"
					popularFeedTimeGranularity={popularFeedTimeGranularity}
					followingFeedSortby={followingFeedSortby}
				>
					{spaceName}
				</LinkOrText>
			)}
		</Flex>
	);
};
